// frontend/src/pages/MyLoansPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MemberLayout from "./MemberLayout";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const MyLoansPage = () => {
  const navigate = useNavigate();
  const [loanStatus, setLoanStatus] = useState(null);
  const [headerStats, setHeaderStats] = useState({
    activeLoansCount: 0,
    totalLoanBalance: 0,
    monthlyDeduction: 0,
    sharesBalance: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchLoanStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const safeNumber = (v) => {
    if (v === undefined || v === null || isNaN(Number(v))) return 0;
    return Number(v);
  };

  const formatKES = (v, decimals = 0) => {
    const num = safeNumber(v);
    return num.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  };

  const computeHeaderStatsFromLoans = (activeLoans = []) => {
    const activeLoansCount = Array.isArray(activeLoans) ? activeLoans.length : 0;
    const totalLoanBalance = Array.isArray(activeLoans)
      ? activeLoans.reduce((sum, l) => sum + safeNumber(l.currentBalance), 0)
      : 0;

    return { activeLoansCount, totalLoanBalance };
  };

  const fetchSharesFromMonthlyOverview = async (memberId, token) => {
    try {
      const month = getCurrentMonth();
      const response = await fetch(`${API_URL}/reports/monthly-overview?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) return 0;

      const data = await response.json();
      if (!data?.success || !data?.data?.members) return 0;

      const memberRow = data.data.members.find((m) => m.memberId === memberId);
      const md = memberRow?.monthData;

      return safeNumber(md?.shares);
    } catch (e) {
      return 0;
    }
  };

  const fetchMonthlyDeductionFromStatement = async (memberId, token) => {
    try {
      const month = getCurrentMonth();

      const response = await fetch(
        `${API_URL}/reports/member-statement/${memberId}?fromMonth=${month}&toMonth=${month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) return 0;

      const data = await response.json();
      if (!data?.success) return 0;

      // Prefer statement row totalDeductions for the selected month
      const row = Array.isArray(data?.data?.statement) ? data.data.statement[0] : null;
      const monthTotal = safeNumber(row?.totalDeductions);
      if (monthTotal > 0) return monthTotal;

      // fallback
      return safeNumber(data?.data?.summary?.grandTotalDeductions);
    } catch (e) {
      return 0;
    }
  };

  const fetchLoanStatus = async () => {
    try {
      const token = localStorage.getItem("hopeSaccoToken");
      const storedUser = localStorage.getItem("hopeSaccoUser");

      if (!token || !storedUser) {
        navigate("/login");
        return;
      }

      const userData = JSON.parse(storedUser);

      const allowedRoles = ['member', 'admin', 'official'];
      if (!allowedRoles.includes(userData.role)) {
        navigate('/login');
        return;
      }

      const memberId = userData.profile?.memberId;

      if (!memberId) {
        setError("Member ID not found");
        setIsLoading(false);
        return;
      }

      const loansResponse = await fetch(`${API_URL}/loans/members/${memberId}/loan-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!loansResponse.ok) {
        throw new Error("Failed to fetch loan status");
      }

      const loansData = await loansResponse.json();

      if (!loansData.success) {
        setLoanStatus(null);
        setError(loansData.message || "Failed to fetch loan status");
        setIsLoading(false);
        return;
      }

      const payload = loansData.data || {};
      const safeStatus = {
        memberInfo: payload.memberInfo || { memberId: "", fullName: "" },
        activeLoans: payload.activeLoans || [],
        completedLoans: payload.completedLoans || [],
        summary: payload.summary || {
          totalActiveLoans: 0,
          totalCompletedLoans: 0,
          totalCurrentBalance: 0,
          totalMonthlyDeduction: 0,
          memberShares: 0,
          memberSavings: 0,
        },
      };

      setLoanStatus(safeStatus);

      const computed = computeHeaderStatsFromLoans(safeStatus.activeLoans);

      const [sharesBalance, monthlyDeduction] = await Promise.all([
        fetchSharesFromMonthlyOverview(memberId, token),
        fetchMonthlyDeductionFromStatement(memberId, token),
      ]);

      setHeaderStats({
        activeLoansCount: computed.activeLoansCount,
        totalLoanBalance: computed.totalLoanBalance,
        monthlyDeduction: monthlyDeduction,
        sharesBalance: sharesBalance,
      });
    } catch (err) {
      console.error("Error:", err);
      setLoanStatus(null);
      setError("Failed to fetch loan status");
    } finally {
      setIsLoading(false);
    }
  };

  const getLoanTypeColor = (loanType) => {
    switch (loanType) {
      case "normal":
        return "#3498db";
      case "school":
        return "#e67e22";
      case "emergency":
        return "#e74c3c";
      default:
        return "#95a5a6";
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      if (isNaN(d.getTime())) return "N/A";
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch (e) {
      return "N/A";
    }
  };

  // ✅ TRUE completion percentage (paid %)
  // completion = (amount - balance) / amount * 100
  const calcCompletionPercent = (amount, balance) => {
    const a = safeNumber(amount);
    const b = safeNumber(balance);
    if (a <= 0) return null;

    let p = ((a - b) / a) * 100;

    // clamp 0..100
    if (!isFinite(p)) return null;
    if (p < 0) p = 0;
    if (p > 100) p = 100;

    return p;
  };

  if (isLoading) {
    return (
      <MemberLayout activePage="loans" pageTitle="My Loans">
        <div className="loading-state">
          <div className="spinner-large"></div>
          <p>Loading your loans...</p>
        </div>
      </MemberLayout>
    );
  }

  if (error) {
    return (
      <MemberLayout activePage="loans" pageTitle="My Loans">
        <div className="error-page">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={() => navigate("/member/dashboard")}>
            Back to Dashboard
          </button>
        </div>
      </MemberLayout>
    );
  }

  return (
    <MemberLayout
      activePage="loans"
      pageTitle="My Loans"
      breadcrumbs={[
        { label: "Dashboard", link: "/member/dashboard" },
        { label: "My Loans" },
      ]}
    >
      <div className="page-content">
        {loanStatus && (
          <>
            {/* Summary Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "1rem",
                marginBottom: "2rem",
              }}
            >
              <div className="stat-card" style={{ background: "#e3f2fd" }}>
                <div className="stat-label">Active Loans</div>
                <div className="stat-value" style={{ color: "#1976d2" }}>
                  {headerStats.activeLoansCount}
                </div>
              </div>

              <div className="stat-card" style={{ background: "#fce4ec" }}>
                <div className="stat-label">Total Loan Balance</div>
                <div className="stat-value" style={{ color: "#c2185b" }}>
                  KES {formatKES(headerStats.totalLoanBalance, 2)}
                </div>
              </div>

              <div className="stat-card" style={{ background: "#fff3e0" }}>
                <div className="stat-label">Monthly Deduction</div>
                <div className="stat-value" style={{ color: "#f57c00" }}>
                  KES {formatKES(headerStats.monthlyDeduction, 2)}
                </div>
              </div>

              <div className="stat-card" style={{ background: "#e8f5e9" }}>
                <div className="stat-label">My Shares</div>
                <div className="stat-value" style={{ color: "#388e3c" }}>
                  KES {formatKES(headerStats.sharesBalance, 2)}
                </div>
              </div>
            </div>

            {/* Active Loans */}
            {loanStatus.activeLoans.length > 0 && (
              <div className="card">
                <h2 style={{ marginTop: 0 }}>Active Loans</h2>
                <div className="table-responsive">
                  <table className="data-table">
                    <thead>
                      <tr>
                        {/* ✅ same as before, except Interest removed */}
                        <th>Loan Type</th>
                        <th>Amount</th>
                        <th>Balance</th>
                        <th>Monthly Payment</th>
                        {/* ❌ Interest removed */}
                        <th>Disbursed</th>
                        <th>Completion %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loanStatus.activeLoans.map((loan, index) => {
                        const completion = calcCompletionPercent(
                          loan.originalAmount,
                          loan.currentBalance
                        );

                        return (
                          <tr key={index}>
                            <td>
                              <span
                                style={{
                                  padding: "4px 12px",
                                  borderRadius: "12px",
                                  background: getLoanTypeColor(loan.loanType),
                                  color: "white",
                                  fontSize: "0.875rem",
                                  fontWeight: "500",
                                }}
                              >
                                {loan.loanType?.charAt(0).toUpperCase() +
                                  loan.loanType?.slice(1) || "Unknown"}
                              </span>
                            </td>

                            <td>KES {formatKES(loan.originalAmount, 2)}</td>
                            <td>KES {formatKES(loan.currentBalance, 2)}</td>

                            {/* ✅ Monthly Payment reads from header Monthly Deduction */}
                            <td>KES {formatKES(headerStats.monthlyDeduction, 2)}</td>

                            <td>{formatDate(loan.disbursementDate)}</td>

                            <td>{completion === null ? "N/A" : `${completion.toFixed(0)}%`}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Completed Loans (unchanged columns, interest not shown) */}
            {loanStatus.completedLoans.length > 0 && (
              <div className="card" style={{ marginTop: "2rem" }}>
                <h2 style={{ marginTop: 0 }}>Completed Loans</h2>
                <div className="table-responsive">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Loan Type</th>
                        <th>Amount</th>
                        <th>Disbursed</th>
                        <th>Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loanStatus.completedLoans.map((loan, index) => (
                        <tr key={index}>
                          <td>
                            <span
                              style={{
                                padding: "4px 12px",
                                borderRadius: "12px",
                                background: getLoanTypeColor(loan.loanType),
                                color: "white",
                                fontSize: "0.875rem",
                                fontWeight: "500",
                              }}
                            >
                              {loan.loanType?.charAt(0).toUpperCase() +
                                loan.loanType?.slice(1) || "Unknown"}
                            </span>
                          </td>
                          <td>KES {formatKES(loan.originalAmount, 2)}</td>
                          <td>{formatDate(loan.disbursementDate)}</td>
                          <td>{formatDate(loan.completionDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* No Active Loans */}
            {loanStatus.activeLoans.length === 0 && (
              <div className="card">
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <p style={{ fontSize: "3em" }}>✅</p>
                  <h3>No Active Loans</h3>
                  <p>You have no active loans at the moment.</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </MemberLayout>
  );
};

export default MyLoansPage;
